import React, { useEffect, useRef, useState } from "react";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { Link } from "react-router-dom";
import "./Blogs.css";

function Blogs() {
  document.title = "Blogs :: D-Right Technologies";

  const [blogs, setBlogs] = useState([]);
  const [isDesktop, setIsDesktop] = useState(false);
  const [page, setPage] = useState(1);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(3);
  const featuredRef = useRef(null);
  const morePostsRef = useRef(null);

  useEffect(() => {
    fetch("/blogs.json")
      .then((response) => response.json())
      .then((data) => setBlogs(data.blogs))
      .catch((error) => console.error("Error fetching blogs:", error));
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 992px)");
    const apply = () => setIsDesktop(mq.matches);
    apply();

    // Safari support for older listeners
    if (mq.addEventListener) mq.addEventListener("change", apply);
    else mq.addListener(apply);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", apply);
      else mq.removeListener(apply);
    };
  }, []);

  const featuredBlog = blogs?.[0] || null;
  const remainingBlogs = blogs.slice(1);

  const DESKTOP_PAGE_SIZE = 6;
  const MOBILE_COLLAPSED_COUNT = 3;
  const MOBILE_STEP = 3;

  const totalPages = Math.max(
    1,
    Math.ceil(remainingBlogs.length / DESKTOP_PAGE_SIZE)
  );

  const visibleMorePosts = isDesktop
    ? remainingBlogs.slice(
        (page - 1) * DESKTOP_PAGE_SIZE,
        (page - 1) * DESKTOP_PAGE_SIZE + DESKTOP_PAGE_SIZE
      )
    : remainingBlogs.slice(0, Math.min(mobileVisibleCount, remainingBlogs.length));

  const scrollToElTop = (el) => {
    window.requestAnimationFrame(() => {
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const scrollToFeaturedTop = () => scrollToElTop(featuredRef.current);
  const scrollToMorePostsTop = () => scrollToElTop(morePostsRef.current);

  useEffect(() => {
    // Reset UX state when switching modes or when data changes
    setPage(1);
    setMobileVisibleCount(MOBILE_COLLAPSED_COUNT);
  }, [isDesktop, blogs.length]);

  return (
    <div>
      <Header />
      <div className="main no-banner-top-padding">
        <section
          className="page-header-section ptb-100 bg-image"
          image-overlay="8"
        >
          <div
            className="background-image-wraper"
            style={{
              background: `url('/assets/img/slider-bg-1.jpg')`,
              opacity: "1",
            }}
          ></div>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-9 col-lg-7">
                <div className="page-header-content text-white pt-4">
                  <h1 className="text-white mb-0">Blogs</h1>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="our-blog-section ptb-100">
          <div className="container blogs-surface">
            {blogs.length > 0 ? (
              <>
                {featuredBlog && page === 1 ? (
                  <div className="blogs-featured-final">
                    <div
                      className="blogs-featured-final__hero blogs-modern-card"
                      ref={featuredRef}
                    >
                      <div className="blogs-featured-final__media">
                        <img
                          src={`${featuredBlog.image}`}
                          className="blogs-featured-final__img"
                          alt={featuredBlog.title}
                        />
                      </div>
                      <div className="blogs-featured-final__body">
                        <div className="blogs-featured-final__meta">
                          <span className="blogs-featured-final__date">
                            {new Date(featuredBlog.date).toLocaleDateString(
                              "en-US",
                              { month: "short", day: "2-digit", year: "numeric" }
                            )}
                          </span>
                        </div>
                        <h2 className="blogs-featured-final__title">
                          <Link to={`/blog/${featuredBlog.slug}`}>
                            {featuredBlog.title}
                          </Link>
                        </h2>
                        <p className="blogs-featured-final__desc">{featuredBlog.desc}</p>
                        <Link to={`/blog/${featuredBlog.slug}`} className="detail-link">
                          Read full story <i className="fas fa-arrow-right" aria-hidden="true"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : null}

                {remainingBlogs.length ? (
                  <div className="blogs-featured-final__section" ref={morePostsRef}>
                    <div className="blogs-featured-final__section-title">
                      More posts
                    </div>
                    <div className="row">
                      {visibleMorePosts.map((blog, index) => (
                        <div key={index} className="col-md-6 col-lg-4">
                          <div className="single-blog-card card gray-light-bg border-0 shadow-sm my-3 blogs-modern-card">
                            <div className="blog-img position-relative">
                              <img
                                src={`${blog.image}`}
                                className="card-img-top"
                                alt={blog.title}
                              />
                              <div className="meta-date">
                                <strong>{new Date(blog.date).getDate()}</strong>
                                <small>
                                  {new Date(blog.date).toLocaleString("default", {
                                    month: "short",
                                  })}
                                </small>
                              </div>
                            </div>
                            <div className="card-body">
                              <h3 className="h5 mb-2 card-title">
                                <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                              </h3>
                              <p className="card-text">{blog.desc}</p>
                              <Link to={`/blog/${blog.slug}`} className="detail-link">
                                Read more <i className="fas fa-arrow-right" aria-hidden="true"></i>
                              </Link>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {isDesktop ? (
                      totalPages > 1 ? (
                        <div className="blogs-pagination">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setPage((p) => {
                                const nextPage = Math.max(1, p - 1);
                                if (nextPage === 1) scrollToFeaturedTop();
                                else scrollToMorePostsTop();
                                return nextPage;
                              });
                            }}
                            disabled={page === 1}
                          >
                            Prev
                          </button>
                          <div className="blogs-pagination__meta">
                            Page {page} of {totalPages}
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setPage((p) => {
                                const nextPage = Math.min(totalPages, p + 1);
                                scrollToMorePostsTop();
                                return nextPage;
                              });
                            }}
                            disabled={page === totalPages}
                          >
                            Next
                          </button>
                        </div>
                      ) : null
                    ) : remainingBlogs.length > MOBILE_COLLAPSED_COUNT ? (
                      <div className="blogs-mobile-toggle">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            setMobileVisibleCount((current) => {
                              if (current >= remainingBlogs.length) {
                                return MOBILE_COLLAPSED_COUNT; // View less
                              }
                              return Math.min(
                                remainingBlogs.length,
                                current + MOBILE_STEP
                              ); // View more (append)
                            });
                          }}
                        >
                          {mobileVisibleCount >= remainingBlogs.length
                            ? "View less"
                            : "View more"}
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : (
              <p className="text-center">Loading blogs...</p>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}

export default Blogs;
